import { Button } from '@/shared/ui/button';
import { EMPTY_PLACEHOLDER } from '@/shared/format';
import { useAssistantDraftFor } from '@/modules/assistant/hooks/use-assistant-draft';
import { useAssistantDraftStore } from '@/modules/assistant/shared/assistant-store';
import {
  assistantDraftActionsClasses,
  assistantDraftClasses,
  assistantDraftFieldClasses,
  assistantDraftFieldNameClasses,
  assistantDraftFieldValueClasses,
  assistantDraftHeadingClasses,
  assistantDraftReviewClasses,
} from '@/modules/assistant/parts/styles';
import type { AssistantDraft } from '@/api/schemas/assistant';

export type DraftHandoverProps = {
  /** The capability this form accepts. A draft for another capability is not offered. */
  readonly capability: string;
  /**
   * The names of the fields this form HAS.
   *
   * The form's own field names decide what a draft may fill, so a draft that names a field
   * this form does not have cannot write into it. The names are also what lets this notice
   * TELL the person that part of the proposal went nowhere, rather than dropping it
   * silently.
   */
  readonly fieldNames: readonly string[];
  /** Called with the draft when the person chooses to fill the form with it. */
  readonly onApply: (draft: AssistantDraft) => void;
};

/**
 * THE HAND-OVER — the form's side of a published draft.
 *
 * ---------------------------------------------------------------------------
 * WHY THE PERSON PRESSES A SECOND BUTTON
 * ---------------------------------------------------------------------------
 * The panel publishes a draft; this notice is what the form shows when one is waiting. The
 * person then presses "Fill the form", and only then are the values written.
 *
 * That second press is deliberate. The person has already reviewed the values in the panel,
 * but the form is where the values LAND — and a form that filled itself the instant a draft
 * was published would move values into fields the person is looking at, possibly while they
 * are typing in one of them. So the hand-over is explicit, and the values are shown here
 * again, at the place they will land.
 *
 * ---------------------------------------------------------------------------
 * IT WRITES NOTHING
 * ---------------------------------------------------------------------------
 * This notice has no API call in it. It hands the draft to the form, the form puts the
 * values into ITS OWN state, and the ordinary form performs the ordinary write with the
 * ordinary validation. The assistant never writes.
 */
export function DraftHandover({ capability, fieldNames, onApply }: DraftHandoverProps) {
  const draft = useAssistantDraftFor(capability);
  const clearDraft = useAssistantDraftStore((state) => state.clearDraft);

  if (draft === null) return null;

  const skipped = Object.keys(draft.fields).filter((name) => !fieldNames.includes(name));

  return (
    <section className={assistantDraftClasses} aria-label={`Proposed values for ${capability}`}>
      <p className={assistantDraftHeadingClasses}>Lakshi proposed values for this form</p>

      {/* The server's own review sentence, shown verbatim — the same one the panel showed. */}
      <p className={assistantDraftReviewClasses}>{draft.review}</p>

      <dl>
        {Object.entries(draft.fields).map(([name, value]) => (
          <div key={name} className={assistantDraftFieldClasses}>
            <dt className={assistantDraftFieldNameClasses}>{name}</dt>
            <dd className={assistantDraftFieldValueClasses}>
              {value === '' ? EMPTY_PLACEHOLDER : value}
            </dd>
          </div>
        ))}
      </dl>

      {skipped.length > 0 && (
        <p className="text-sm text-content-muted">
          Not applied: {skipped.join(', ')}. This form has no such field.
        </p>
      )}

      <div className={assistantDraftActionsClasses}>
        <Button
          size="sm"
          onClick={() => {
            onApply(draft);
            clearDraft();
          }}
        >
          Fill the form
        </Button>
        <Button size="sm" variant="ghost" onClick={clearDraft}>
          Discard
        </Button>
      </div>
    </section>
  );
}
